import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { useIntl } from 'react-intl'
import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import DualSliderInput from 'LuxKit/Slider/DualSliderInput'
import Group from 'components/utils/Group'
import debounce from 'lodash.debounce'
import { Experiments } from 'constants/experiments'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useExperiment from 'hooks/Experiments/useExperiment'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { FlightsFiltersPriceType } from 'lib/flights/flightFilterTypes'
import { getCombinedPriceHistogramBuckets, getPriceHistogramBuckets } from 'lib/flights/flightFilterUtils'
import FlightPriceHistogram from './FlightPriceHistogram'
import FlightsFilterSegmentedControl from './FlightsFilterSegmentedControl'

interface Props {
  items: Array<FilterPanelCheckItem>
  journeys?: Array<App.JourneyV2>
  combinedJourneys?: Array<App.FlightCombinedJourney>
  shouldRenderCombinedTiles?: boolean
  onFilterChange: (e: ChangeEvent<HTMLInputElement>) => void
  onPriceTypeChange: (price: FlightsFiltersPriceType) => void
  selectedMaxItem?: string
  selectedMinItem?: string
  priceType: FlightsFiltersPriceType
}

const STEP_NUM = 100

function FlightSingleFilterPrice(props: Props) {
  const intl = useIntl()
  const {
    items,
    journeys = [],
    combinedJourneys,
    shouldRenderCombinedTiles,
    onFilterChange,
    selectedMaxItem,
    selectedMinItem,
    priceType,
    onPriceTypeChange,
  } = props

  const isSingleLegRedesignEnabled = useFeatureFlag(OptimizelyFeatureFlags.flightsSingleLegRedesign)
  const { isTurnedOn: isCombinedReturnLegsEnabled } = useExperiment(Experiments.flightsCombinedReturnLegs, {
    canActivate: false,
  })
  const hidePriceTypeToggle = isSingleLegRedesignEnabled || isCombinedReturnLegsEnabled
  const effectivePriceType = hidePriceTypeToggle ? FlightsFiltersPriceType.ADULT : priceType

  const maxNum = (items[items.length - 1]?.value as number) || 0
  const minNum = (items[0]?.value as number) || 0
  const min = Math.floor(minNum / STEP_NUM) * STEP_NUM
  const max = Math.ceil(maxNum / STEP_NUM) * STEP_NUM

  const [startValue, setStartValue] = useState(
    selectedMinItem ? parseInt(selectedMinItem) : min,
  )
  const [endValue, setEndValue] = useState(
    selectedMaxItem ? parseInt(selectedMaxItem) : max,
  )

  const histogramBuckets = useMemo(() => {
    if (shouldRenderCombinedTiles && combinedJourneys) {
      return getCombinedPriceHistogramBuckets(effectivePriceType, combinedJourneys, min, max)
    }
    return getPriceHistogramBuckets(effectivePriceType, journeys, min, max)
  }, [combinedJourneys, effectivePriceType, journeys, max, min, shouldRenderCombinedTiles])

  const perAdultLabel = intl.formatMessage({
    id: 'SF1c4uS6a1gaPEDtoKdm',
    defaultMessage: 'Per adult',
    description: 'Tab label for per-adult price filter on flight search',
  })
  const totalPriceLabel = intl.formatMessage({
    id: 'eKHf1w0g3uJqkBJLaEJN',
    defaultMessage: 'Total price',
    description: 'Tab label for total price filter on flight search in the flights section',
  })

  const segmentedOptions = useMemo(() => ([
    { value: FlightsFiltersPriceType.ADULT, label: perAdultLabel },
    { value: FlightsFiltersPriceType.TOTAL, label: totalPriceLabel },
  ]), [perAdultLabel, totalPriceLabel])

  useEffect(() => {
    if (hidePriceTypeToggle && priceType !== FlightsFiltersPriceType.ADULT) {
      onPriceTypeChange(FlightsFiltersPriceType.ADULT)
    }
  }, [hidePriceTypeToggle, onPriceTypeChange, priceType])

  useEffect(() => {
    if (!selectedMinItem) {
      setStartValue(min)
    }
  }, [selectedMinItem, min])

  useEffect(() => {
    if (!selectedMaxItem) {
      setEndValue(max)
    }
  }, [selectedMaxItem, max])

  const onChange = useMemo(() => {
    return debounce((e: ChangeEvent<HTMLInputElement>) => {
      onFilterChange(e)
    }, 200)
  }, [onFilterChange])

  const handleStartChange = (e: ChangeEvent<HTMLInputElement>) => {
    setStartValue(e.target.valueAsNumber)
    onChange(e)
  }

  const handleEndChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEndValue(e.target.valueAsNumber)
    onChange(e)
  }

  const handlePriceTypeChange = (value: string) => {
    onPriceTypeChange(value as FlightsFiltersPriceType)
  }

  return (
    <Group direction="vertical" gap={16}>
      {!hidePriceTypeToggle && (
        <FlightsFilterSegmentedControl
          inputsName="flight-price-type"
          value={priceType}
          options={segmentedOptions}
          onChange={handlePriceTypeChange}
        />
      )}

      <Group direction="vertical" gap={0} fullWidth horizontalAlign="center">
        <FlightPriceHistogram buckets={histogramBuckets} />

        <DualSliderInput
          variant="label"
          name="price"
          min={min}
          max={max}
          step={STEP_NUM}
          startValue={startValue}
          endValue={endValue}
          onStartChange={handleStartChange}
          onEndChange={handleEndChange}
          format="currency"
        />
      </Group>
    </Group>
  )
}

export default FlightSingleFilterPrice
